import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { createRoot } from "react-dom/client";
import { Provider, useDispatch } from "react-redux";
import csvIcon from "../assets/img/csvicon.svg";
import {
  addDownloadToLog,
  addSuccessDownloadToLog,
  completedDownload,
} from "../Layouts/Navbar/DownloadProgress/DownloadLogSlice";
import { isArray, kindOf, showToast } from "../lib/utility";
import { REPORTSERVERTIME } from "../lib/utils";
import { middleware, store } from "../redux/store";
import FileDownloadConfirm from "../components/Common/FileDownloadConfirm";
import { getInternalPathFromLocation } from "../lib/routeTokens";
import { resolveAbacPathsForLocation } from "../components/User/Abac/abacRouteMap";
import useAbacAccess from "./useAbacAccess";

/**
 * Multi-access ABAC for report downloads. The report route maps to its ABAC
 * node (e.g. sms.reports.summary.delivery); the "download" leaf under it gates
 * the download button. Missing leaf / non multi-access users stay allowed, and
 * `explicitPath` (["a","b"]) overrides the route lookup.
 */
export const useAbacDownloadAllowed = (explicitPath) => {
  const location = useLocation();
  const { canAccess } = useAbacAccess();
  const paths = explicitPath
    ? [explicitPath]
    : resolveAbacPathsForLocation(getInternalPathFromLocation(location));
  if (!paths?.length) return true;
  return paths.some((path) => canAccess([...path, "download"]));
};

const confirmModalHandler = ({
  currId,
  data,
  fileType,
  reportName,
  chartsRef,
  mappable,
}) => {
  const container = document.createElement("div");
  container.id = "file-download-confirm-container";
  document.body.appendChild(container);
  const root = createRoot(container);

  root.render(
    <Provider store={store}>
      <FileDownloadConfirm
        {...{
          currId,
          data,
          fileType,
          reportName,
          chartsRef,
          mappable,
        }}
      />
    </Provider>
  );
};

export const usePaginationNDownload = (
  getReportData,
  reportData,
  downloadOptions = {}
) => {
  let {
    getReportThunk,
    reportName,
    filterData,
    searchVal,
    mappable,
    selector,
    chartsRef,
    contentField = "data",
    onPageChange,
    onPerPageChange,
    initialPageSize = 50,
    abacPath,
  } = downloadOptions;
  const canDownload = useAbacDownloadAllowed(abacPath);
  let downloadFactor = REPORTSERVERTIME[reportName] ?? 0.04;

  const [pagination, setPagination] = useState({
    current: 0,
    size: initialPageSize,
    isLastPage: false,
    totalPages: 0,
  });
  const [fileType, setFileType] = useState({
    value: "csv",
    label: "CSV",
    image: csvIcon,
  });

  const dispatch = useDispatch();

  const handlePageChange = (page) => {
    if (
      pagination.current + 1 !== page &&
      page <= pagination.totalPages
    ) {
      setPagination((prev) => ({ ...prev, current: page - 1 }));
      onPageChange && onPageChange(page);

      kindOf(getReportData, "function") &&
        getReportData(undefined, undefined, page - 1);
    }
  };
  const handlePerPageChange = (size) => {
    if (pagination.size !== size) {
      const page = 0;
      setPagination((prev) => ({ ...prev, size: size, current: page }));
      onPerPageChange && onPerPageChange(size);
      kindOf(getReportData, "function") &&
        getReportData(undefined, undefined, page, size);
    }
  };
  const downloadContent = () => {
    if (!canDownload) return;
    if (pagination.totalPages === 0) {
      showToast("No Data to download", "warning");
      return;
    }
    let currId = Date.now();
    const controller = new AbortController();

    dispatch(
      addDownloadToLog({
        name: `${reportName}`,
        id: currId,
        totalTime: pagination.totalItems * downloadFactor + 400 + 200,
        type: fileType.value,
        url: window.location.href,
        controller: controller,
      })
    );

    dispatch(
      getReportThunk(
        {
          udata: filterData,
          search: searchVal,
          page: 0,
          size: pagination.totalItems,
          download: currId,
          controller: controller,
        },
        { signal: controller.signal }
      )
    ).then((action) =>
      middleware(
        action,
        reportName,
        (action) => {
          if (isArray(action.payload?.[contentField])) {
            const raw = selector
              ? selector(action.payload)
              : action.payload?.[contentField];
            const normalizedRows = isArray(raw?.[0]) ? raw[0] : raw;

            let data = mappable(normalizedRows);

            confirmModalHandler({
              currId,
              data,
              fileType,
              reportName,
              chartsRef,
              mappable,
            });

            if (fileType.value) {
              dispatch(
                completedDownload({
                  name: `${reportName} ${fileType.value}`,
                  id: currId,
                })
              );
            }
          }
        },
        "Error while Downloading " + reportName
      )
    );
  };

  return {
    pagination,
    setPagination,
    fileType,
    setFileType,
    handlePageChange,
    handlePerPageChange,
    // undefined hides the download button in CommonHeader
    downloadContent: canDownload ? downloadContent : undefined,
    canDownload,
  };
};

export const useDownloadData = (reportData, downloadOptions) => {
  let { reportName, mappable, chartsRef, paginationDefaultSize, abacPath } =
    downloadOptions ?? {};
  const canDownload = useAbacDownloadAllowed(abacPath);

  const paginationSize = downloadOptions ? paginationDefaultSize ?? 50 : 10;

  const [pagination, setPagination] = useState({
    current: 0,
    size: paginationSize,
    isLastPage: false,
    totalItems: reportData.length,
    totalPages: Math.ceil(reportData.length / paginationSize),
  });
  const [fileType, setFileType] = useState({
    value: "csv",
    label: "CSV",
    image: csvIcon,
  });

  const dispatch = useDispatch();

  const handlePageChange = (page) => {
    if (
      pagination.current + 1 !== page &&
      reportData.length !== 0 &&
      page <= pagination.totalPages
    ) {
      setPagination((prev) => ({ ...prev, current: page - 1 }));
    }
  };
  const handlePerPageChange = (size) => {
    if (pagination.size !== size) {
      let page = pagination.current;
      if (pagination.current * size > pagination.totalItems)
        page = parseInt(pagination.totalItems / size);

      setPagination((prev) => ({
        ...prev,
        size: size,
        current: page,
        totalPages: Math.ceil(reportData.length / paginationSize),
      }));
    }
  };
  const downloadContent = () => {
    if (!canDownload) return;
    const data = mappable(reportData);
    if (!isArray(data)) {
      showToast("Bad Table Data", "warning");
      return;
    }
    if (data.length === 0) {
      showToast("No Data to download", "warning");
      return;
    }

    confirmModalHandler({
      data,
      fileType,
      reportName,
      chartsRef,
      mappable,
    });
    let dataLength = JSON.stringify(data).length;

    dispatch(
      addSuccessDownloadToLog({
        name: `${reportName}`,
        id: Date.now(),
        totalTime: data.length,
        type: fileType.value,
        url: window.location.href,
        size: dataLength * 1.05,
      })
    );
  };

  useEffect(() => {
    const totalPages = Math.ceil(reportData.length / paginationSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      current:
        prev.current + 1 > totalPages
          ? Math.max(0, totalPages - 1)
          : prev.current,
      totalItems: reportData.length,
    }));
  }, [reportData.length]);

  return {
    fileType,
    pagination,
    setPagination,
    setFileType,
    // undefined hides the download button in CommonHeader
    downloadContent: canDownload ? downloadContent : undefined,
    canDownload,
    handlePageChange,
    handlePerPageChange,
  };
};
